import React, { useMemo, useState } from "react";
import { Row, Col, Card, CardBody, Table, CardTitle } from "reactstrap";
import { useTable, usePagination, useGlobalFilter } from "react-table";
import Pagi2 from "common/utils/Pagi2";
import { CommonTableContainer } from "common/utils/ComonTableContainer";
import moment from "moment";

const globalFilterFunction = (rows, columnIds, filterValue) => {
  return rows.filter((row) => {
    return columnIds.some((columnId) => {
      const cellValue = row.values[columnId];
      return String(cellValue ?? "")
        .toLowerCase()
        .includes(String(filterValue).toLowerCase());
    });
  });
};

function TableContainer({ columns, data }) {
  const [fixedHeader, setFixedHeader] = useState(false);

  // Use the state and functions returned from useTable to build your UI
  const {
    state,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize,
    pageOptions,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      globalFilter: globalFilterFunction,
      initialState: {
        pageIndex: 0,
        sortBy: [
          {
            id: "id",
            desc: true,
          },
        ],
      },
    },
    useGlobalFilter,
    usePagination
  );

  const { pageIndex, pageSize, globalFilter } = state;

  document.addEventListener("scroll", () => {
    if (window.scrollY > 165) {
      setFixedHeader(true);
    } else {
      setFixedHeader(false);
    }
  });

  // Render the UI for your table
  return (
    <div>
      <Row className="mb-3 justify-content-between">
        <Col md={2}>
          <select
            className="border-radius form-select"
            style={{ width: "100px" }}
            value={pageSize}
            onChange={(e) => setPageSize(e.target.value)}
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </Col>
        <Col md={8} className="d-flex justify-content-end">
          <div>
            <div className="search-box me-xxl-2 my-xxl-0 d-inline-block">
              <div className="position-relative">
                <label htmlFor="search-bar-0" className="search-label">
                  <span id="search-bar-0-label" className="sr-only">
                    Search this table
                  </span>
                  <input
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    id="search-bar-0"
                    type="text"
                    className="border-radius form-control rounded h-100"
                    placeholder={` Search...`}
                    value={globalFilter || ""}
                  />
                </label>
                <i className="bx bx-search-alt search-icon"></i>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <CommonTableContainer columns={columns} data={data}  />

      {/* Pagination */}
      <Row className="justify-content-md-end justify-content-center align-items-center">
        <Pagi2
          gotoPage={gotoPage}
          previousPage={previousPage}
          canPreviousPage={canPreviousPage}
          nextPage={nextPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
        />
      </Row>
    </div>
  );
}

const ViewSupport = ({ customer_support }) => {
  // table columns
  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "support_id",
        isInfoIcon: false,
        className: "support_id",
        Cell: (cellprops) => {
          return <div className="text-center">{cellprops.row.original.id}</div>;
        },
      },
      {
        Header: "Assinged",
        accessor: "support_assigned",
        isInfoIcon: false,
        className: "support_assigned",
        Cell: (cellprops) => {
          return (
            <div className="text-center">
              {cellprops.row.original.assigned || "-"}
            </div>
          );
        },
      },
      {
        Header: "Subject",
        accessor: "support_subject",
        isInfoIcon: false,
        className: "support_subject",
        Cell: (cellprops) => {
          return (
            <div className="text-center">
              {cellprops.row.original.subject || "-"}
            </div>
          );
        },
      },
      {
        Header: "Project",
        accessor: "support_project",
        isInfoIcon: false,
        className: "support_project",
        Cell: (cellprops) => {
          return (
            <div className="text-center">
              {cellprops.row.original.project_name || "-"}
            </div>
          );
        },
      },
      {
        Header: "Priority",
        accessor: "support_priority",
        isInfoIcon: false,
        className: "support_priority",
        Cell: (cellprops) => {
          const status = cellprops.row.original.priority;
          return status == "High" ? (
            <span
              className="badge bg-danger text-white"
              style={{ fontSize: "12px" }}
            >
              {cellprops.row.original.support_priority}
            </span>
          ) : status == "Medium" ? (
            <span
              className="badge bg-warning text-white"
              style={{ fontSize: "12px" }}
            >
              {cellprops.row.original.support_priority}
            </span>
          ) : status == "Low" ? (
            <span
              className="badge bg-primary text-white"
              style={{ fontSize: "12px" }}
            >
              {cellprops.row.original.support_priority}
            </span>
          ) : null;
        },
      },
      {
        Header: "Status",
        accessor: "support_status",
        isInfoIcon: false,
        className: "support_status",
        Cell: (cellprops) => {
          return (
            <div className="text-center">
              {cellprops.row.original.status || "-"}
            </div>
          );
        },
      },
      {
        Header: "Last Reply",
        accessor: "support_reply",
        isInfoIcon: false,
        className: "support_reply",
        Cell: (cellprops) => {
          return (
            <div className="text-center">
              {moment(cellprops.row.original.updated_at.slice(0, 10)).format(
                "DD-MM-YYYY"
              )}
            </div>
          );
        },
      },
      {
        Header: "Created",
        accessor: "support_created",
        isInfoIcon: false,
        className: "support_created",
        Cell: (cellprops) => {
          return (
            <div className="text-center">
              {moment(cellprops.row.original.created_at.slice(0, 10)).format(
                "DD-MM-YYYY"
              )}
            </div>
          );
        },
      },

      {
        Header: "Action",
        accessor: "support_action",
        isInfoIcon: false,
        className: "support_action",
        Cell: (cellprops) => {
          return (
            <a className="btn btn-sm btn-outline-secondary me-1" title="View">
              <i className="fas fa-eye"></i>
            </a>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <div>
        <div>
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <CardTitle className="mb-3 text-dark">Support</CardTitle>
                  <div className="responsive-table">
                    <TableContainer columns={columns} data={customer_support} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ViewSupport;
